import React, { useCallback, useEffect, useRef, useState } from 'react';
import TickBox from '../components/TickBox';
import PhotoTile from '../components/PhotoTile';
import Drawer from '../components/Drawer';
import PhotoViewer from '../components/PhotoViewer';
import PhotoMasonry from '../components/PhotoMasonry';
import PlaylistPane from './PlaylistPane';
import Loader from '../../Loader/Loader';
import { fetchCollectionMedia } from '../state/api';
import { getBriefIds } from '../state/session';
import { logoUrl } from '../runtime';
import { useDevice } from '../../hooks/useDevice';
const sameId = (a, b) => String(a) === String(b);

// Signed server-side and expiring, so it is used exactly as given. An empty
// one means the card has no face; nothing is constructed to stand in for it.
const coverOf = option => option.cover_url || '';

// A collection's photo URLs are signed the same way, for a day. Held pages
// are read again well before that, so a card reopened in a tab left open
// overnight does not show a grid of broken tiles.
const MEDIA_TTL_MS = 60 * 60 * 1000;
const isStale = held => Date.now() - held.fetchedAt > MEDIA_TTL_MS;

// What an option looks like is decided by what the server sent with it, not by
// which section it happens to sit in.
const visualOf = option => {
  if (option.embed_url) return 'embed';
  // `has_media` says there are photos behind the card without carrying
  // them; the preview payload still sends `media` outright.
  if (option.has_media || (option.media || []).length || coverOf(option)) {
    return 'photo';
  }
  return 'plain';
};

/** A `list` field that carries options: a capped picker. */
const OptionPicker = ({
  field,
  picked,
  locked,
  isPhone,
  onToggle
}) => {
  const [openId, setOpenId] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(null);
  const options = field.options || [];
  const cap = field.max_picks || 0;
  const atLimit = cap > 0 && picked.length >= cap;
  const isOn = option => picked.some(x => sameId(x, option.id));
  const open = options.find(o => sameId(o.id, openId)) || null;
  const openVisual = open ? visualOf(open) : null;
  // One collection's photos, a page at a time, read when the card is
  // opened and continued as the client scrolls. Kept per option id so
  // closing and reopening the same card does not ask for it all again,
  // until the signed URLs are near their expiry (see MEDIA_TTL_MS).
  const [loaded, setLoaded] = useState({});
  // Per collection, not one flag for the picker: a client who closes one
  // card and opens another would otherwise have the first card's reply
  // clear the second card's spinner, and the second would show its empty
  // state while its own photos were still on the way.
  const [loadingIds, setLoadingIds] = useState({});
  // Which page a collection last failed on. A read that fails is not an
  // empty collection, and the client is told the difference.
  const [failedIds, setFailedIds] = useState({});
  // Same breakpoints the column-count rules used: 4, then 3, then 2.
  // Those rules were max-width 640px and 1100px; useDevice compares with
  // `<`, so each bound is one pixel higher.
  const { isPhone: phone, narrow } = useDevice(641, 1101);
  const columnCount = phone ? 2 : narrow ? 3 : 4;
  const key = String(openId);
  const held = loaded[key];
  const loading = !!loadingIds[key];
  const failedPage = failedIds[key];
  const failed = failedPage !== undefined;
  // The preview payload carries its photos outright; nothing to fetch.
  // By length: an empty `media` is not photos, and must not hide the rows
  // that were fetched.
  const media = open?.media?.length ? open.media : held?.rows || [];
  const sentinel = useRef(null);

  const readPage = useCallback(page => {
    if (!open || !open.has_media || open.media?.length) return;
    const { bookingId, briefUuid } = getBriefIds();
    if (!bookingId || !briefUuid) return;
    const id = String(open.id);

    setLoadingIds(prev => ({ ...prev, [id]: true }));
    setFailedIds(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    fetchCollectionMedia({ bookingId, briefUuid, optionRef: open.id, page })
      .then(({ media: rows, nextPage }) => setLoaded(prev => {
        const prior = page === 0 ? [] : prev[id]?.rows || [];
        // Timed from the first page: later pages were signed after it, so
        // they lapse no sooner.
        const fetchedAt = page === 0 ? Date.now() : prev[id]?.fetchedAt;
        // An empty page is not the end: the server steps past a collection's
        // empty deliverable that way. A cursor that does not move is, or the
        // sentinel, still in view, would ask for the same page forever.
        const stuck = nextPage !== null && String(nextPage) === String(page);
        return {
          ...prev,
          [id]: {
            rows: prior.concat(rows),
            nextPage: stuck ? null : nextPage,
            fetchedAt
          }
        };
      }))
      // Remember which page failed so the client can ask for that one
      // again rather than losing the pages already on screen.
      .catch(() => setFailedIds(prev => ({ ...prev, [id]: page })))
      .then(() => setLoadingIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      }));
  }, [open]);

  // First page on open, and again when what is held has gone stale. The
  // stale rows are dropped first so the loader shows rather than tiles
  // whose URLs have lapsed. A card closed and reopened while its read is
  // still out holds nothing yet, but that read will fill it; a second one
  // would only repeat it.
  useEffect(() => {
    if (!open || loading || (held && !isStale(held))) return;
    if (held) {
      setLoaded(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    readPage(0);
    // Only when a different card opens. `held` and `loading` are checked
    // inline, and adding them would re-run this on every page that lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  // Later pages as the end of the list comes into view. A sentinel rather
  // than a scroll handler, so nothing runs on every scroll event.
  useEffect(() => {
    const el = sentinel.current;
    const more = held?.nextPage;
    if (!el || more === null || more === undefined || loading || failed) {
      return undefined;
    }
    // Observed against the drawer body, which is what scrolls. Against the
    // viewport the margin is cut off at the drawer's edge, so the next page
    // would only be asked for once the sentinel was already showing.
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) readPage(more);
    }, { root: el.closest('.cp-drawer-body'), rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [held, loading, failed, readPage]);

  // Nothing here has a cover or an embed, so it renders as plain tick cards.
  if (!options.some(o => visualOf(o) !== 'plain')) {
    return React.createElement("div", {
      className: "cp-pickgrid"
    }, options.map(option => {
      const on = isOn(option);
      const blocked = locked || atLimit && !on;
      return React.createElement("button", {
        key: option.id,
        type: "button",
        className: `cp-pick${on ? ' is-on' : ''}${blocked && !on ? ' is-dim' : ''}`,
        disabled: blocked,
        onClick: () => onToggle(option.id)
      }, React.createElement(TickBox, {
        checked: on
      }), React.createElement("span", {
        className: "cp-pick-text"
      }, React.createElement("strong", null, option.name), option.blurb && React.createElement("span", null, option.blurb)));
    }));
  }
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-cardgrid"
  }, options.map(option => React.createElement(PhotoTile, {
    key: option.id,
    name: option.name,
    cover: coverOf(option),
    showView: visualOf(option) !== 'plain',
    checked: isOn(option),
    atLimit: atLimit,
    locked: locked,
    onOpen: () => setOpenId(option.id),
    onToggle: () => !locked && onToggle(option.id)
  }))), React.createElement(Drawer, {
    open: !!open,
    title: open?.name,
    meta: open?.blurb || (cap ? `${picked.length} / ${cap} ${field.label}` : ''),
    checked: open ? isOn(open) : false,
    tickLabel: open && isOn(open) ? 'Selected' : 'Select',
    locked: locked || atLimit && open && !isOn(open),
    fill: openVisual === 'embed',
    onToggle: () => open && !locked && onToggle(open.id),
    onClose: () => setOpenId(null)
  }, openVisual === 'embed' && React.createElement(PlaylistPane, {
    option: open
  }), openVisual === 'photo' && (loading && !media.length ? React.createElement(Loader, {
    inline: true,
    logoSrc: logoUrl(),
    label: ""
  }) : failed && !media.length ? React.createElement("button", {
    type: "button",
    className: "cp-retrymedia",
    onClick: () => readPage(failedPage)
  }, "Couldn't load photos. Tap to retry.") : media.length ? React.createElement(React.Fragment, null, React.createElement(PhotoMasonry, {
    media: media,
    columnCount: columnCount,
    onOpen: setPhotoIndex
  }),
  // Below the grid rather than inside it: the masonry is a column layout,
  // so its last child is not its lowest point on screen.
  React.createElement("div", {
    ref: sentinel,
    className: "cp-masonry-end",
    "aria-hidden": "true"
  }), failed && React.createElement("button", {
    type: "button",
    className: "cp-retrymedia",
    onClick: () => readPage(failedPage)
  }, "Couldn't load more photos. Tap to retry.")) :
  
  // Ops has not pointed this collection at a source booking yet.
  React.createElement("p", {
    className: "cp-emptymedia"
  }, "No references have been added to this collection yet."))), React.createElement(PhotoViewer, {
    items: media,
    index: photoIndex,
    onIndex: setPhotoIndex,
    isPhone: isPhone,
    onClose: () => setPhotoIndex(null)
  }));
};
export default OptionPicker;
